import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { View, Text, ScrollView, TextInput, TouchableOpacity, StyleSheet, Platform, RefreshControl, Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import Table from '../components/common/Table';
import UserEditModal from '../components/modals/UserEditModal';
import api, { getData, deleteData, FINAL_API_URL } from '../../services/api';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../theme/adminTheme';

const UserManagementScreen = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, [page, roleFilter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            console.log('[UserManagement] Fetching users (page:', page, 'filter:', roleFilter, ')');

            const data = await getData('/admin/users', {
                page,
                limit: 25,
                search: searchQuery,
                role: roleFilter,
                _t: Date.now() // Cache buster
            });

            if (data && data.success) {
                console.log('[UserManagement] Fetch success:', data.data.users?.length, 'users');
                setUsers(data.data.users || []);
                setTotalPages(data.data.pagination?.totalPages || 1);
            } else {
                console.error('[UserManagement] Operation failed:', data?.error || 'Unknown server error');
                alert(data?.error || 'Server error occurred');
            }
        } catch (error) {
            console.error('[UserManagement] Fetch error detailed:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Network error';
            alert(`Failed to load users: ${errorMsg}`);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchUsers();
    };

    const handleAddUser = () => {
        setIsAddMode(true);
        setSelectedUser({});
        setIsEditModalVisible(true);
    };

    const handleEditUser = (user) => {
        setIsAddMode(false);
        setSelectedUser(user);
        setIsEditModalVisible(true);
    };

    const handleExport = async () => {
        try {
            console.log('[UserManagement] Exporting users...');
            const apiUrl = await AsyncStorage.getItem('apiUrl') || FINAL_API_URL;
            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
            
            const exportUrl = `${apiUrl}admin/users/export?token=${token}`;
            Linking.openURL(exportUrl);
        } catch (error) {
            console.error('[UserManagement] Export error:', error);
            alert('Failed to export data');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!confirm('Are you sure you want to delete this user?')) return;

        try {
            console.log('[UserManagement] Deleting user:', userId);
            const data = await deleteData(`/admin/users/${userId}`);

            if (data.success) {
                alert('User deleted successfully');
                fetchUsers();
            } else {
                alert(data.error || 'Failed to delete user');
            }
        } catch (error) {
            console.error('[UserManagement] Error deleting user:', error);
            const errorMsg = error.response?.data?.error || 'Failed to delete user';
            alert(`Error: ${errorMsg}`);
        }
    };

    const onRefresh = () => {
        setRefreshing(true);
        fetchUsers();
    };

    const columns = [
        { field: 'id', header: 'ID', width: 60, sortable: true },
        { field: 'name', header: 'Name', width: 180, sortable: true },
        { field: 'email', header: 'Email', width: 220, sortable: true },
        {
            field: 'occupation',
            header: 'Occupation',
            width: 120,
            render: (value) => <Text style={styles.cellTextNormal}>{value ? value.charAt(0).toUpperCase() + value.slice(1) : 'N/A'}</Text>
        },
        {
            field: 'organization',
            header: 'Organization',
            width: 180,
            render: (_, user) => <Text style={styles.cellTextNormal}>{user.participant?.fcp?.name || 'N/A'}</Text>
        },
        {
            field: 'keNumber',
            header: 'Participant Number',
            width: 130,
            render: (_, user) => <Text style={styles.cellTextNormal}>{user.participant?.participantNumber || 'N/A'}</Text>
        },
        {
            field: 'role',
            header: 'Role',
            width: 100,
            render: (value) => (
                <View style={[
                    styles.roleBadge,
                    value === 'admin' ? styles.adminBadge :
                        value === 'mentor' ? styles.mentorBadge : styles.userBadge
                ]}>
                    <Text style={[
                        styles.roleText,
                        value === 'admin' ? styles.adminText :
                            value === 'mentor' ? styles.mentorText : styles.userText
                    ]}>
                        {value?.toUpperCase() || 'UNKNOWN'}
                    </Text>
                </View>
            )
        },
        {
            field: 'profileType',
            header: 'Profile Type',
            width: 150,
            render: (_, user) => <Text style={styles.cellTextNormal}>{user.role === 'admin' ? 'Admin' : 'Standard'}</Text>
        },
        {
            field: 'createdAt',
            header: 'Registered',
            width: 150,
            render: (value) => {
                if (!value) return <Text style={styles.cellTextNormal}>N/A</Text>;
                const date = new Date(value);
                const options = { month: 'short', day: 'numeric', year: 'numeric' };
                return <Text style={styles.cellTextNormal}>{date.toLocaleDateString('en-US', options)}</Text>;
            }
        },
        {
            field: 'status',
            header: 'Status',
            width: 120,
            render: (value) => (
                <View style={[
                    styles.statusBadge,
                    value === 'active' ? styles.statusActive :
                        value === 'suspended' ? styles.statusSuspended :
                            value === 'pending' ? styles.statusPending : styles.statusInactive
                ]}>
                    <View style={[
                        styles.statusDot,
                        {
                            backgroundColor: value === 'active' ? getColor('success.500') :
                                value === 'suspended' ? getColor('danger.500') :
                                    value === 'pending' ? getColor('warning.500') : getColor('gray.500')
                        }
                    ]} />
                    <Text style={[
                        styles.statusText,
                        {
                            color: value === 'active' ? getColor('success.700') :
                                value === 'suspended' ? getColor('danger.700') :
                                    value === 'pending' ? getColor('warning.700') : getColor('gray.700')
                        }
                    ]}>
                        {value?.toUpperCase() || 'UNKNOWN'}
                    </Text>
                </View>
            )
        },
        {
            field: 'actions',
            header: 'Actions',
            width: 120,
            align: 'right',
            render: (_, user) => (
                <View style={styles.actionsContainer}>
                    <TouchableOpacity
                        style={styles.minimalActionButton}
                        onPress={() => handleEditUser(user)}
                    >
                        <Ionicons name="create-outline" size={20} color={getColor('primary.600')} />
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={styles.minimalActionButton}
                        onPress={() => handleDeleteUser(user.id)}
                    >
                        <Ionicons name="trash-outline" size={20} color={getColor('danger.600')} />
                    </TouchableOpacity>
                </View>
            )
        }
    ];

    return (
        <AdminLayout>
            <ScrollView
                style={styles.container}
                contentContainerStyle={styles.contentContainer}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                }
            >
                {/* Header */}
                <View style={styles.header}>
                    <View>
                        <Text style={styles.headerTitle}>User Management</Text>
                        <Text style={styles.headerSubtitle}>
                            Manage all registered users and their permissions
                        </Text>
                    </View>
                    <View style={styles.headerRight}>
                        <TouchableOpacity
                            style={[styles.addButton, styles.exportButton]}
                            onPress={handleExport}
                        >
                            <Ionicons name="download-outline" size={18} color="#FFFFFF" />
                            <Text style={styles.addButtonText}>Export</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={styles.addButton}
                            onPress={handleAddUser}
                        >
                            <Ionicons name="person-add-outline" size={18} color="#FFFFFF" />
                            <Text style={styles.addButtonText}>Add User</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Filters */}
                <View style={[styles.filtersCard, getShadow('md')]}>
                    <View style={styles.filtersRow}>
                        {/* Search */}
                        <View style={styles.searchContainer}>
                            <Ionicons name="search" size={20} color={getColor('gray.400')} style={styles.searchIcon} />
                            <TextInput
                                style={styles.searchInput}
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChangeText={setSearchQuery}
                                onSubmitEditing={handleSearch}
                            />
                        </View>

                        <View style={styles.filterGroup}>
                            <Text style={styles.filterLabel}>Filter By Role:</Text>
                            <View style={styles.filterList}>
                                {['', 'admin', 'mentor', 'youth'].map((role) => (
                                    <TouchableOpacity
                                        key={role}
                                        style={[
                                            styles.filterItem,
                                            roleFilter === role && styles.filterItemActive
                                        ]}
                                        onPress={() => {
                                            setRoleFilter(role);
                                            setPage(1);
                                        }}
                                    >
                                        <Text style={[
                                            styles.filterButtonText,
                                            roleFilter === role && styles.filterButtonTextActive
                                        ]}>
                                            {role || 'All'}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>

                        {/* Search Button */}
                        <TouchableOpacity
                            style={styles.searchButton}
                            onPress={handleSearch}
                        >
                            <Ionicons name="search" size={20} color="#FFFFFF" />
                            <Text style={styles.searchButtonText}>Search</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Users Table */}
                <View style={[styles.tableCard, getShadow('md')]}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.tableTitle}>All Users</Text>
                        <Text style={styles.tableSubtitle}>
                            {users.length} users found
                        </Text>
                    </View>

                    <Table
                        columns={columns}
                        data={users}
                        loading={loading}
                        onEdit={handleEditUser}
                        onDelete={handleDeleteUser}
                        itemsPerPage={25}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <View style={styles.pagination}>
                            <TouchableOpacity
                                style={[styles.paginationButton, page === 1 && styles.paginationButtonDisabled]}
                                onPress={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <Ionicons name="chevron-back" size={20} color={page === 1 ? getColor('gray.400') : getColor('primary.600')} />
                            </TouchableOpacity>

                            <Text style={styles.paginationText}>
                                Page {page} of {totalPages}
                            </Text>

                            <TouchableOpacity
                                style={[styles.paginationButton, page === totalPages && styles.paginationButtonDisabled]}
                                onPress={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                <Ionicons name="chevron-forward" size={20} color={page === totalPages ? getColor('gray.400') : getColor('primary.600')} />
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
            </ScrollView>

            <UserEditModal
                visible={isEditModalVisible}
                onClose={() => {
                    setIsEditModalVisible(false);
                    setIsAddMode(false);
                }}
                user={selectedUser}
                isAddMode={isAddMode}
                onUpdate={fetchUsers}
            />
        </AdminLayout>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: getColor('gray.50'),
    },
    contentContainer: {
        padding: getSpacing('lg'),
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: getSpacing('xl'),
    },
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: getSpacing('sm'),
        backgroundColor: getColor('primary.600'),
        paddingHorizontal: getSpacing('lg'),
        paddingVertical: 10,
        borderRadius: getBorderRadius('md'),
        ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
    },
    addButtonText: {
        color: '#FFFFFF',
        fontWeight: '600',
        fontSize: 14,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    headerTitle: {
        fontSize: 28,
        fontWeight: '700',
        color: getColor('gray.900'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    headerSubtitle: {
        fontSize: 14,
        color: getColor('gray.600'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    filtersCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        marginBottom: getSpacing('lg'),
    },
    filtersRow: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        gap: getSpacing('md'),
        alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    },
    searchContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: getColor('gray.50'),
        borderRadius: getBorderRadius('md'),
        paddingHorizontal: getSpacing('md'),
        minWidth: Platform.OS === 'web' ? 300 : '100%',
    },
    searchIcon: {
        marginRight: getSpacing('sm'),
    },
    searchInput: {
        flex: 1,
        height: 44,
        fontSize: 14,
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif', outline: 'none' } : {}),
    },
    filterGroup: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        alignItems: Platform.OS === 'web' ? 'center' : 'flex-start',
        gap: getSpacing('sm'),
    },
    filterLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.700'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    filterItemActive: {
        backgroundColor: getColor('primary.600'),
        borderColor: getColor('primary.600'),
    },
    filterButtonText: {
        fontSize: 13,
        fontWeight: '500',
        color: getColor('gray.600'),
        textTransform: 'capitalize',
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    filterButtonTextActive: {
        color: '#FFFFFF',
    },
    headerRight: {
        flexDirection: 'row',
        gap: getSpacing('md'),
    },
    exportButton: {
        backgroundColor: getColor('success.600'),
    },
    filterList: {
        flexDirection: 'row',
        gap: getSpacing('xs'),
    },
    filterItem: {
        paddingHorizontal: getSpacing('md'),
        paddingVertical: 6,
        borderRadius: getBorderRadius('full'),
        backgroundColor: getColor('gray.100'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
    },
    filterItemActive: {
        backgroundColor: getColor('primary.600'),
        borderColor: getColor('primary.600'),
    },
    searchButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: getSpacing('sm'),
        backgroundColor: getColor('primary.600'),
        paddingHorizontal: getSpacing('lg'),
        paddingVertical: 12,
        borderRadius: getBorderRadius('md'),
        ...(Platform.OS === 'web' ? {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: getColor('primary.700'),
            },
        } : {}),
    },
    searchButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    tableCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
    },
    tableHeader: {
        marginBottom: getSpacing('lg'),
        paddingBottom: getSpacing('md'),
        borderBottomWidth: 1,
        borderBottomColor: getColor('gray.200'),
    },
    tableTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: getColor('gray.900'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    tableSubtitle: {
        fontSize: 14,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    actionsContainer: {
        flexDirection: 'row',
        gap: getSpacing('xs'),
    },
    actionButton: {
        width: 32,
        height: 32,
        borderRadius: getBorderRadius('md'),
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' ? {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        } : {}),
    },
    editButton: {
        backgroundColor: getColor('primary.600'),
    },
    deleteButton: {
        backgroundColor: getColor('danger.600'),
    },
    roleBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: getBorderRadius('full'),
        alignSelf: 'flex-start',
    },
    roleText: {
        fontSize: 10,
        fontWeight: '700',
    },
    adminBadge: {
        backgroundColor: getColor('primary.50'),
    },
    adminText: {
        color: getColor('primary.700'),
    },
    userBadge: {
        backgroundColor: getColor('gray.100'),
    },
    userText: {
        color: getColor('gray.700'),
    },
    mentorBadge: {
        backgroundColor: '#EEF2FF',
    },
    mentorText: {
        color: '#4F46E5',
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: getBorderRadius('full'),
        alignSelf: 'flex-start',
        gap: 6,
    },
    statusDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
    },
    statusText: {
        fontSize: 10,
        fontWeight: '700',
    },
    cellTextNormal: {
        fontSize: 13,
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    minimalActionButton: {
        padding: 6,
        borderRadius: 6,
        ...(Platform.OS === 'web' ? {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: getColor('gray.100'),
            },
        } : {}),
    },
    statusActive: {
        backgroundColor: getColor('success.50'),
    },
    statusInactive: {
        backgroundColor: getColor('gray.100'),
    },
    statusSuspended: {
        backgroundColor: getColor('danger.50'),
    },
    statusPending: {
        backgroundColor: getColor('warning.50'),
    },
    pagination: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: getSpacing('lg'),
        marginTop: getSpacing('lg'),
        paddingTop: getSpacing('lg'),
        borderTopWidth: 1,
        borderTopColor: getColor('gray.200'),
    },
    paginationButton: {
        width: 36,
        height: 36,
        borderRadius: getBorderRadius('md'),
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: getColor('gray.100'),
        ...(Platform.OS === 'web' ? {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: getColor('gray.200'),
            },
        } : {}),
    },
    paginationButtonDisabled: {
        opacity: 0.5,
        ...(Platform.OS === 'web' ? {
            cursor: 'not-allowed',
        } : {}),
    },
    paginationText: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.700'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
});

export default UserManagementScreen;
